import { Dialog as HeadlessDialog } from '@headlessui/react';
import { ReactNode, useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  showCloseButton?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  disableManualClose?: boolean;
  className?: string;
  isSticky?: boolean;
  insideScroll?: boolean;
  hide?: boolean;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export default function Dialog({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  maxWidth = '2xl',
  closeOnClickOutside = true,
  closeOnEsc = true,
  disableManualClose = false,
  className = '',
  isSticky = false,
  insideScroll = false,
  hide = false,
}: DialogProps) {
  const [maxHeight, setMaxHeight] = useState('auto');

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (closeOnEsc && !disableManualClose && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);

      // Set max height based on window height
      const updateMaxHeight = () => {
        const windowHeight = window.innerHeight;
        setMaxHeight(`${windowHeight - (insideScroll ? 42 : 100)}px`);
      };

      updateMaxHeight();
      window.addEventListener('resize', updateMaxHeight);

      return () => {
        document.removeEventListener('keydown', handleEsc);
        window.removeEventListener('resize', updateMaxHeight);
      };
    }
  }, [isOpen, closeOnEsc, onClose, disableManualClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (
      closeOnClickOutside &&
      !disableManualClose &&
      e.target === e.currentTarget
    ) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <HeadlessDialog
          open={isOpen}
          onClose={() => {}}
          className="relative z-50"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className={`fixed inset-0 bg-black/30 ${hide ? 'hidden' : ''}`}
            aria-hidden="true"
            onClick={handleBackdropClick}
          />

          <div
            className={`fixed inset-0 flex items-center justify-center p-4 ${hide ? 'hidden' : ''}`}
            onClick={handleBackdropClick}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.1 }}
              className={`mx-auto w-full rounded-2xl bg-white ${isSticky ? 'px-5' : 'p-5'} ${maxWidthClasses[maxWidth]} ${className}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxHeight: maxHeight,
                overflowY: 'auto',
              }}
            >
              {(title || showCloseButton) && (
                <div className={isSticky ? 'sticky top-0 z-10 bg-white pt-4 pb-1' : ''}>
                <HeadlessDialog.Title className="flex flex-row items-center gap-2 justify-between font-medium mb-4">
                  {title && <div>{title}</div>}
                  {showCloseButton && !disableManualClose && (
                    <button onClick={onClose}>
                      <XIcon size={20} color="#096499" />
                    </button>
                  )}
                </HeadlessDialog.Title>
                </div>
              )}
              {children}
            </motion.div>
          </div>
        </HeadlessDialog>
      )}
    </AnimatePresence>
  );
}
